import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { appRoutes } from '@tekman/onmat/constants';
import { GroupsFacade } from '@tekman/onmat/store/group';
import { StorageService } from '@tekman/shared/service';
import { SubSink } from '@tekman/shared/utils';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { SelectorService } from '../services/selector.service';
import { Group } from '@tekman/onmat/models';
import { ProfileFacade } from '@tekman/onmat/store/profile';

@Component({
  selector: 'onmat-group-selector',
  templateUrl: './group-selector.component.html',
  styleUrls: ['./group-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupSelectorComponent implements OnDestroy, OnInit {
  @Input() active: boolean;
  @Input() disabled = false;
  groups$ = this.groupFacade.groups$;
  groupSelectedId: string;
  private subscriptions: SubSink = new SubSink();
  private url: string;
  groups: Group[] = [];

  constructor(
    private readonly groupFacade: GroupsFacade,
    private readonly localStorage: StorageService,
    private readonly profileFacade: ProfileFacade,
    private readonly _router: Router,
    private selectorService: SelectorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.profileFacade.userInfo$.pipe(filter(userInfo => !!userInfo)).subscribe(userInfo => {
      const selectedAcademicYearId = this.localStorage.selectedAcademicYear;
      this.groupFacade.loadGroupsByYear(selectedAcademicYearId);

    });
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          this.url = event.url;
        }),
        switchMap(() => this.groups$),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current))
      )
      .subscribe(groups => {
        this.groups = groups;
        this.cdr.detectChanges();

        if (this.groups.length) {
          const urlSplit = this.url.split('/').filter(value => !!value);
          const urlId = urlSplit[1];
          let groupId: string;
          if (!urlSplit[0] || urlSplit[0]?.includes(appRoutes.itineraries)) {
            const foundGroup = groups.find(group => group.id === urlId);
            groupId = foundGroup ? foundGroup.id || this.localStorage.selectedGroup : this.localStorage.selectedGroup || groups[0].id;
          } else {
            groupId = this.localStorage.selectedGroup === 'undefined' ? groups[0].id : this.localStorage.selectedGroup;
          }
          this.onSelectionChanged(groupId, true);
        }
      });
  }

  onSelectionChanged(groupId: string, updateGroups: boolean) {
    this.groupSelectedId = groupId;
    this.selectorService.emitGroupChange(this.groupSelectedId);

    if (this.url.includes(appRoutes.login) || this.url.includes(appRoutes.groups) || this.url.includes(appRoutes.activities)) {
      return;
    }

    if (
      updateGroups &&
      !this.url.includes(appRoutes.steps) &&
      !this.url.includes(appRoutes.resources) &&
      !this.url.includes(appRoutes.contents) &&
      !this.url.includes(appRoutes.tracking)
    ) {
      this._router.navigate([appRoutes.itineraries, this.groupSelectedId]);
    } else if (!updateGroups) {
      const currentRoute = this.url.split('/')[1];
      this._router.navigate([currentRoute, this.groupSelectedId]);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
